/**
 * Official Copilot Studio governance/admin API client.
 *
 * These operations use the global Power Platform API 2024-10-01 surface and
 * require a delegated user token with CopilotStudio.AdminActions.Invoke.
 */

import type { AuthProvider } from "../auth/types.js";
import { RESOURCES } from "../auth/types.js";
import {
  ApiError,
  AuthenticationError,
  AuthorizationError,
  RateLimitError,
  wrapApiCall,
} from "../utils/errors.js";
import { readResponseBody, requestWithRetry } from "../utils/http.js";
import { sanitizeErrorMessage, validateEnvironmentId, validateRowId } from "../utils/security.js";

export const COPILOT_ADMIN_ORIGIN = "https://api.powerplatform.com";
export const COPILOT_ADMIN_API_VERSION = "2024-10-01";
export const COPILOT_ADMIN_PERMISSION = "CopilotStudio.AdminActions.Invoke";

const MAX_RESPONSE_BYTES = 1024 * 1024;
const MAX_ERROR_DETAIL_CHARS = 16_384;

export interface CopilotAdminApiConfig {
  authProvider: AuthProvider;
  defaultEnvironmentId: string;
}

export interface CopilotAdminTarget {
  environmentId: string;
  agentId: string;
}

export interface CopilotQuarantineStatus extends CopilotAdminTarget {
  isQuarantined: boolean;
  lastUpdateTimeUtc?: string;
}

export interface CopilotConnectorConsentBypassStatus extends CopilotAdminTarget {
  adminConsentBypass: boolean;
}

export interface CopilotOwnerReassignment extends CopilotAdminTarget {
  newOwnerAadUserId: string;
  reassigned: true;
}

type CopilotAdminMethod = "GET" | "POST" | "PUT";

interface CopilotAdminRequest {
  method: CopilotAdminMethod;
  url: string;
  context: string;
  body?: Record<string, unknown>;
}

function asObject(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : undefined;
}

function safeErrorDetail(value: string): string | undefined {
  if (!value) return undefined;
  const redacted = sanitizeErrorMessage(value)
    .replace(/\bBearer\s+[A-Za-z0-9._~+/-]+={0,2}/gi, "Bearer [REDACTED]")
    .replace(/([?&](?:api[_-]?key|key|token|secret|password|sig)=)[^&\s"']+/gi, "$1[REDACTED]")
    .slice(0, MAX_ERROR_DETAIL_CHARS);
  return redacted || undefined;
}

function safeDateTime(value: unknown): string | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== "string" || value.length === 0 || value.length > 100) {
    throw new ApiError("Copilot Studio quarantine response has an invalid update time", 502);
  }
  if (
    Array.from(value).some((character) => {
      const codePoint = character.codePointAt(0) ?? 0;
      return codePoint <= 0x1f || codePoint === 0x7f;
    })
  ) {
    throw new ApiError("Copilot Studio quarantine response has an invalid update time", 502);
  }
  if (Number.isNaN(Date.parse(value))) {
    throw new ApiError("Copilot Studio quarantine response has an invalid update time", 502);
  }
  return value;
}

function requireBoolean(source: Record<string, unknown>, field: string, context: string): boolean {
  const value = source[field];
  if (typeof value !== "boolean") {
    throw new ApiError(`${context}: response is missing boolean ${field}`, 502);
  }
  return value;
}

function parseQuarantineStatus(
  value: unknown,
  target: CopilotAdminTarget,
): CopilotQuarantineStatus {
  const source = asObject(value);
  if (!source) {
    throw new ApiError("Copilot Studio quarantine response is not an object", 502);
  }
  const result: CopilotQuarantineStatus = {
    ...target,
    isQuarantined: requireBoolean(source, "isBotQuarantined", "Copilot Studio quarantine"),
  };
  const lastUpdateTimeUtc = safeDateTime(source.lastUpdateTimeUtc);
  if (lastUpdateTimeUtc) result.lastUpdateTimeUtc = lastUpdateTimeUtc;
  return result;
}

function parseConnectorConsentBypass(
  value: unknown,
  target: CopilotAdminTarget,
): CopilotConnectorConsentBypassStatus {
  const source = asObject(value);
  if (!source) {
    throw new ApiError("Copilot Studio connector consent-bypass response is not an object", 502);
  }
  return {
    ...target,
    adminConsentBypass: requireBoolean(
      source,
      "adminConsentBypass",
      "Copilot Studio connector consent bypass",
    ),
  };
}

/** Client for the documented Copilot Studio administrator operations. */
export class CopilotAdminApi {
  private readonly authProvider: AuthProvider;
  private readonly defaultEnvironmentId: string;

  constructor(config: CopilotAdminApiConfig) {
    this.authProvider = config.authProvider;
    this.defaultEnvironmentId = validateEnvironmentId(config.defaultEnvironmentId);
  }

  private target(agentId: string, environmentId?: string): CopilotAdminTarget {
    return {
      environmentId: validateEnvironmentId(environmentId ?? this.defaultEnvironmentId),
      agentId: validateRowId(agentId).toLowerCase(),
    };
  }

  private url(target: CopilotAdminTarget, suffix: string): string {
    return (
      `${COPILOT_ADMIN_ORIGIN}/copilotstudio/environments/` +
      `${encodeURIComponent(target.environmentId)}/bots/${encodeURIComponent(target.agentId)}` +
      `/api/${suffix}?api-version=${COPILOT_ADMIN_API_VERSION}`
    );
  }

  private async headers(hasBody: boolean): Promise<Record<string, string>> {
    const token = await this.authProvider.getTokenForResource(RESOURCES.POWER_PLATFORM_API);
    return {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      ...(hasBody ? { "Content-Type": "application/json" } : {}),
    };
  }

  private async throwForError(
    response: Awaited<ReturnType<typeof requestWithRetry>>,
    context: string,
  ): Promise<void> {
    if (response.statusCode < 400) return;
    if (response.statusCode === 429) {
      const retryAfter = response.headers["retry-after"];
      await response.body.text().catch(() => undefined);
      const seconds = Number(Array.isArray(retryAfter) ? retryAfter[0] : retryAfter);
      throw new RateLimitError(Number.isFinite(seconds) ? seconds * 1_000 : undefined);
    }

    const { statusCode, text } = await readResponseBody(response, context, MAX_RESPONSE_BYTES);
    const detail = safeErrorDetail(text);
    if (statusCode === 401) {
      throw new AuthenticationError(
        `${context}: Power Platform API authentication failed; refresh the signed-in session. ` +
          `If ${COPILOT_ADMIN_PERMISSION} has not been consented, ask a Microsoft Entra ` +
          "administrator to grant it and then refresh the session; re-running --setup cannot grant this consent.",
        detail,
      );
    }
    if (statusCode === 403) {
      if (/InsufficientDelegatedPermissions|CopilotStudio\.AdminActions\.Invoke/i.test(text)) {
        throw new AuthorizationError(
          `${context}: the app registration is missing the Power Platform API delegated ` +
            `permission ${COPILOT_ADMIN_PERMISSION}. Ask a Microsoft Entra administrator to ` +
            "grant admin consent, then refresh the signed-in session; re-running --setup cannot grant this consent.",
          detail,
        );
      }
      throw new AuthorizationError(
        `${context}: access denied. The app registration needs ${COPILOT_ADMIN_PERMISSION}, ` +
          "and the signed-in user needs Power Platform Administrator, AI Administrator, or Global " +
          "Administrator (Environment Administrator is also supported for some reassignment " +
          "operations). Ask an Entra administrator to verify the role and grant consent, then " +
          "refresh the signed-in session; re-running --setup cannot grant this consent.",
        detail,
      );
    }
    if (statusCode === 405 && /quarantine|reassign/i.test(context)) {
      throw new ApiError(
        `${context}: operation is not supported for this agent; quarantine and reassignment ` +
          "do not support classic chatbots",
        statusCode,
        detail,
      );
    }
    throw new ApiError(`${context}: Power Platform API error`, statusCode, detail);
  }

  private async requestJson(request: CopilotAdminRequest): Promise<unknown> {
    return wrapApiCall(async () => {
      const hasBody = request.body !== undefined;
      const response = await requestWithRetry(request.url, {
        method: request.method,
        headers: await this.headers(hasBody),
        ...(hasBody ? { body: JSON.stringify(request.body) } : {}),
      });
      await this.throwForError(response, request.context);
      const { statusCode, text } = await readResponseBody(
        response,
        request.context,
        MAX_RESPONSE_BYTES,
      );
      if (statusCode !== 200) {
        throw new ApiError(
          request.method === "GET"
            ? `${request.context}: expected HTTP 200 but received ${statusCode}`
            : `${request.context}: received unexpected successful HTTP ${statusCode}; a ` +
                "state-changing request might have taken effect, so read the current state before retrying",
          502,
        );
      }
      if (!text) throw new ApiError(`${request.context}: server returned an empty response`, 502);
      try {
        return JSON.parse(text) as unknown;
      } catch (error) {
        throw new ApiError(
          `${request.context}: server returned invalid JSON`,
          502,
          safeErrorDetail(error instanceof Error ? error.message : String(error)),
        );
      }
    }, request.context);
  }

  private async requestNoContent(request: CopilotAdminRequest): Promise<void> {
    return wrapApiCall(async () => {
      const response = await requestWithRetry(request.url, {
        method: request.method,
        headers: await this.headers(request.body !== undefined),
        ...(request.body === undefined ? {} : { body: JSON.stringify(request.body) }),
      });
      await this.throwForError(response, request.context);
      const { statusCode, text } = await readResponseBody(
        response,
        request.context,
        MAX_RESPONSE_BYTES,
      );
      if (statusCode !== 204) {
        throw new ApiError(
          `${request.context}: received unexpected successful HTTP ${statusCode}; ownership might ` +
            "have changed, so verify it before retrying",
          502,
        );
      }
      if (text.trim()) {
        throw new ApiError(`${request.context}: expected an empty response body`, 502);
      }
    }, request.context);
  }

  async getQuarantineStatus(
    agentId: string,
    environmentId?: string,
  ): Promise<CopilotQuarantineStatus> {
    const target = this.target(agentId, environmentId);
    const payload = await this.requestJson({
      method: "GET",
      url: this.url(target, "botQuarantine"),
      context: "Get Copilot Studio agent quarantine status",
    });
    return parseQuarantineStatus(payload, target);
  }

  async setQuarantineStatus(
    agentId: string,
    quarantined: boolean,
    environmentId?: string,
  ): Promise<CopilotQuarantineStatus> {
    if (typeof quarantined !== "boolean") {
      throw new ApiError("Copilot Studio quarantine target must be a boolean", 400);
    }
    const target = this.target(agentId, environmentId);
    const operation = quarantined ? "SetAsQuarantined" : "SetAsUnquarantined";
    const payload = await this.requestJson({
      method: "POST",
      url: this.url(target, `botQuarantine/${operation}`),
      context: quarantined
        ? "Quarantine Copilot Studio agent"
        : "Remove Copilot Studio agent quarantine",
    });
    const result = parseQuarantineStatus(payload, target);
    if (result.isQuarantined !== quarantined) {
      throw new ApiError(
        "Copilot Studio accepted the quarantine request, but its response did not match the " +
          "requested state; read the current quarantine status before retrying",
        502,
      );
    }
    return result;
  }

  async getConnectorConsentBypass(
    agentId: string,
    environmentId?: string,
  ): Promise<CopilotConnectorConsentBypassStatus> {
    const target = this.target(agentId, environmentId);
    const payload = await this.requestJson({
      method: "GET",
      url: this.url(target, "connectorConsentBypass"),
      context: "Get Copilot Studio agent connector consent bypass",
    });
    return parseConnectorConsentBypass(payload, target);
  }

  async setConnectorConsentBypass(
    agentId: string,
    adminConsentBypass: boolean,
    environmentId?: string,
  ): Promise<CopilotConnectorConsentBypassStatus> {
    if (typeof adminConsentBypass !== "boolean") {
      throw new ApiError("Copilot Studio connector consent-bypass target must be a boolean", 400);
    }
    const target = this.target(agentId, environmentId);
    const payload = await this.requestJson({
      method: "PUT",
      url: this.url(target, "connectorConsentBypass"),
      context: "Set Copilot Studio agent connector consent bypass",
      body: { adminConsentBypass },
    });
    const result = parseConnectorConsentBypass(payload, target);
    if (result.adminConsentBypass !== adminConsentBypass) {
      throw new ApiError(
        "Copilot Studio accepted the connector consent-bypass request, but its response did not " +
          "match the requested state; read the current setting before retrying",
        502,
      );
    }
    return result;
  }

  async reassignOwner(
    agentId: string,
    newOwnerAadUserId: string,
    environmentId?: string,
  ): Promise<CopilotOwnerReassignment> {
    const target = this.target(agentId, environmentId);
    const validatedOwnerId = validateRowId(newOwnerAadUserId).toLowerCase();
    await this.requestNoContent({
      method: "POST",
      url: this.url(target, "botAdminOperations/reassign"),
      context: "Reassign Copilot Studio agent owner",
      body: { NewOwnerAadUserId: validatedOwnerId },
    });
    return { ...target, newOwnerAadUserId: validatedOwnerId, reassigned: true };
  }
}
